import React, { useEffect, useState } from 'react';
import { participantAPI } from '../services/api';

/**
 * AccessDenied — shown when a learner tries to access a domain they are not enrolled in.
 *
 * Props:
 *   domainId  — the domain slug (e.g. "football")
 *   reason    — optional extra text
 *   onBack    — callback for the "Go Back" button
 */
export default function AccessDenied({ domainId, reason, onBack }) {
  const domainLabel = domainId
    ? domainId.charAt(0).toUpperCase() + domainId.slice(1).replace(/_/g, ' ')
    : 'this domain';

  const [requestStatus, setRequestStatus] = useState(null); // null | 'pending' | 'approved' | 'rejected' | 'sending'
  const [note, setNote] = useState('');
  const [showNoteBox, setShowNoteBox] = useState(false);
  const [error, setError] = useState(null);

  // Check if the learner already submitted a request for this domain
  useEffect(() => {
    if (!domainId) return;
    participantAPI.getMyEnrollmentRequests()
      .then(({ data }) => {
        const existing = (data.requests || []).find((r) => r.domain === domainId);
        if (existing) setRequestStatus(existing.status);
      })
      .catch(() => {});
  }, [domainId]);

  const handleRequestAccess = async () => {
    setRequestStatus('sending');
    setError(null);
    try {
      const { data } = await participantAPI.requestEnrollment(domainId, note.trim());
      setRequestStatus(data.status || 'pending');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to submit request. Please try again.');
      setRequestStatus(null);
    }
  };

  const statusBanner = () => {
    if (requestStatus === 'pending') {
      return (
        <div style={bannerStyle('#a78bfa', 'rgba(99,102,241,0.12)', 'rgba(99,102,241,0.3)')}>
          <div style={{ fontSize: '1.5rem', marginBottom: 6 }}>⏳</div>
          <strong>Access request submitted</strong>
          <p style={{ marginTop: 4, fontSize: '0.85rem', color: '#cbd5e1' }}>
            Your request is pending coach approval. You'll gain access once it's approved.
          </p>
        </div>
      );
    }
    if (requestStatus === 'approved') {
      return (
        <div style={bannerStyle('#4ade80', 'rgba(34,197,94,0.12)', 'rgba(34,197,94,0.3)')}>
          <div style={{ fontSize: '1.5rem', marginBottom: 6 }}>✅</div>
          <strong>Access approved!</strong>
          <p style={{ marginTop: 4, fontSize: '0.85rem', color: '#cbd5e1' }}>
            You've been enrolled. Refresh the page to enter the domain.
          </p>
          <button
            onClick={() => window.location.reload()}
            style={{ ...actionBtnStyle, background: 'rgba(34,197,94,0.2)', borderColor: 'rgba(34,197,94,0.5)', color: '#4ade80', marginTop: 10 }}
          >
            Refresh & Enter →
          </button>
        </div>
      );
    }
    if (requestStatus === 'rejected') {
      return (
        <div style={bannerStyle('#f87171', 'rgba(239,68,68,0.1)', 'rgba(239,68,68,0.3)')}>
          <div style={{ fontSize: '1.5rem', marginBottom: 6 }}>❌</div>
          <strong>Request not approved</strong>
          <p style={{ marginTop: 4, fontSize: '0.85rem', color: '#cbd5e1' }}>
            Your access request was declined. Contact your coach for more information.
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #0f172a 0%, #1e1b4b 100%)',
      padding: '2rem',
      fontFamily: 'system-ui, sans-serif',
    }}>
      <div style={{
        background: 'rgba(255,255,255,0.05)',
        border: '1px solid rgba(239,68,68,0.3)',
        borderRadius: '1rem',
        padding: '2.5rem',
        maxWidth: 480,
        width: '100%',
        textAlign: 'center',
        color: '#e2e8f0',
      }}>
        <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🔒</div>

        <h2 style={{ fontSize: '1.4rem', fontWeight: 700, marginBottom: '0.5rem', color: '#fca5a5' }}>
          Access Restricted
        </h2>

        {domainId ? (
          <p style={{ color: '#cbd5e1', marginBottom: '0.5rem', lineHeight: 1.6 }}>
            You are not enrolled in the{' '}
            <strong style={{ color: '#e2e8f0' }}>{domainLabel}</strong> domain.
          </p>
        ) : null}

        {reason && (
          <p style={{ color: '#94a3b8', fontSize: '0.875rem', marginBottom: '0.5rem' }}>
            {reason}
          </p>
        )}

        {/* Enrollment request UI only when a concrete domain is missing */}
        {domainId ? statusBanner() : null}

        {domainId && requestStatus === null && !showNoteBox && (
          <div style={{ marginTop: '1.5rem' }}>
            <p style={{ color: '#94a3b8', fontSize: '0.875rem', marginBottom: '1.25rem', lineHeight: 1.6 }}>
              Submit a request to your coach to get access. They will approve or decline it.
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
              <button onClick={() => setShowNoteBox(true)} style={actionBtnStyle}>
                Request Access
              </button>
              {onBack && (
                <button onClick={onBack} style={{ ...actionBtnStyle, background: 'transparent', border: '1px solid rgba(255,255,255,0.15)', color: '#94a3b8' }}>
                  ← Go Back
                </button>
              )}
            </div>
          </div>
        )}

        {!domainId && onBack && (
          <div style={{ marginTop: '1.5rem' }}>
            <button onClick={onBack} style={{ ...actionBtnStyle, background: 'transparent', border: '1px solid rgba(255,255,255,0.15)', color: '#94a3b8' }}>
              ← Go Back
            </button>
          </div>
        )}

        {/* Note input */}
        {requestStatus === null && showNoteBox && (
          <div style={{ marginTop: '1.5rem', textAlign: 'left' }}>
            <label style={{ display: 'block', fontSize: '0.82rem', color: '#94a3b8', marginBottom: 6 }}>
              Add a note to your coach (optional)
            </label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. I play QB for the JV team and want to study coverage reads."
              style={{
                width: '100%',
                minHeight: 80,
                background: 'rgba(255,255,255,0.06)',
                border: '1px solid rgba(255,255,255,0.15)',
                borderRadius: 8,
                padding: '8px 12px',
                color: '#e2e8f0',
                fontSize: '0.875rem',
                resize: 'vertical',
                boxSizing: 'border-box',
              }}
            />
            {error && (
              <p style={{ color: '#f87171', fontSize: '0.82rem', marginTop: 6 }}>{error}</p>
            )}
            <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
              <button
                onClick={handleRequestAccess}
                disabled={requestStatus === 'sending'}
                style={{ ...actionBtnStyle, flex: 1 }}
              >
                {requestStatus === 'sending' ? 'Submitting…' : 'Submit Request'}
              </button>
              <button
                onClick={() => setShowNoteBox(false)}
                style={{ ...actionBtnStyle, background: 'transparent', border: '1px solid rgba(255,255,255,0.15)', color: '#94a3b8' }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Go Back link when request exists */}
        {requestStatus && requestStatus !== 'approved' && onBack && (
          <div style={{ marginTop: '1.25rem' }}>
            <button onClick={onBack} style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: '0.875rem' }}>
              ← Go Back
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

const actionBtnStyle = {
  padding: '0.65rem 1.4rem',
  background: 'rgba(99,102,241,0.2)',
  border: '1px solid rgba(99,102,241,0.4)',
  borderRadius: '0.5rem',
  color: '#a78bfa',
  cursor: 'pointer',
  fontSize: '0.9rem',
  fontWeight: 600,
};

const bannerStyle = (color, bg, border) => ({
  background: bg,
  border: `1px solid ${border}`,
  borderRadius: 10,
  padding: '14px 16px',
  marginTop: 18,
  marginBottom: 4,
  color,
  fontSize: '0.9rem',
});
